import { alpha, darken, lighten } from '@mui/material/styles';
import statusTokens from 'styles/tokens/status';

/**
 * Theme-aware sx factories for the automation wizard.
 *
 * Every colour here resolves through the active MUI palette, so the same sx
 * reads in both assets/theme and assets/theme-dark. The status pairs in
 * styles/tokens/status.js are light-only hex values: they are used as-is in
 * light mode and replaced by palette-derived tints in dark mode, where the raw
 * pastel backgrounds would sit under near-white text.
 *
 * Font sizes are source pixels. The app renders at 80% above 992px
 * (assets/theme/base/appScale.js), so 13px is the floor for captions and 14px
 * for body copy.
 */

const isDark = (theme) => theme.palette.mode === 'dark';

export const FONT = {
  caption: '13px',
  body: '14px',
  title: '16px',
};

/** Outlined text field: fixed 45px height, primary hover/focus outline. */
export const inputSx = (theme) => ({
  '& .MuiInputBase-root': {
    height: '45px',
    borderRadius: '8px',
    bgcolor: 'background.paper',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: theme.palette.outline.variant },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
  },
});

/** Autocomplete input: grows past 45px when it holds tags. */
export const autocompleteInputSx = (theme) => ({
  ...inputSx(theme),
  '& .MuiInputBase-root': {
    minHeight: '45px',
    borderRadius: '8px',
    bgcolor: 'background.paper',
  },
});

/** Same as inputSx without the fixed height, for multiline fields. */
export const multilineInputSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    bgcolor: 'background.paper',
    '& fieldset': { borderColor: theme.palette.outline.variant },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
  },
});

/** Outlined <Select>: the outline lives on .MuiOutlinedInput-notchedOutline. */
export const selectSx = (theme) => ({
  height: '45px',
  borderRadius: '8px',
  bgcolor: 'background.paper',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.outline.variant,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    bgcolor: 'surface.main',
  },
});

/** Floating label that turns primary on focus. */
export const labelSx = {
  '&.Mui-focused': { color: 'primary.main' },
};

/** Placeholder text inside an empty <Select> or a value slot. */
export const placeholderSx = {
  color: 'text.secondary',
  fontStyle: 'italic',
  fontSize: FONT.body,
};

/** Bordered card on the paper surface. `highlighted` gives it a primary edge. */
export const cardSx = (theme, { highlighted = false } = {}) => ({
  borderRadius: '12px',
  border: '1px solid',
  borderColor: highlighted
    ? alpha(theme.palette.primary.main, isDark(theme) ? 0.5 : 0.35)
    : theme.palette.outline.variant,
  bgcolor: 'background.paper',
});

/** Primary-tinted panel, the dark-safe replacement for the old cyan rgba fills. */
export const tintSx = (theme, strength = 0.06) => ({
  bgcolor: alpha(theme.palette.primary.main, isDark(theme) ? strength * 2 : strength),
});

/** Square badge with a number or icon on a solid primary fill. */
export const badgeSx = (size = 32) => ({
  width: size,
  height: size,
  borderRadius: size >= 40 ? '10px' : '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontWeight: 600,
  fontSize: size >= 40 ? FONT.title : FONT.body,
  flexShrink: 0,
});

/** Same badge, muted, for incomplete items. */
export const mutedBadgeSx = (size = 32) => ({
  ...badgeSx(size),
  bgcolor: 'surface.containerHighest',
  color: 'text.secondary',
});

/** Icon tile used in card headers: primary icon on a primary tint. */
export const iconTileSx = (theme, size = 40) => ({
  width: size,
  height: size,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: 'primary.main',
  bgcolor: alpha(theme.palette.primary.main, isDark(theme) ? 0.16 : 0.1),
});

const PALETTE_KEY = {
  success: 'success',
  warning: 'warning',
  info: 'primary',
  error: 'error',
};

/**
 * Background/text pair for a status kind (success, warning, info, error,
 * neutral). Light mode uses the styles/tokens/status.js pairs, except that
 * the success and info text tokens fall short of 4.5:1 on their own pastel
 * backgrounds (about 3.6:1 and 4.4:1), so their text is darkened here
 * (success about 5.7:1, info about 5.6:1). Warning, error and neutral already
 * clear 4.5:1. Dark mode tints the palette colour and lightens the text so it
 * clears 4.5:1 on the dark paper.
 */
const LIGHT_TEXT_DARKEN = { success: 0.25, info: 0.15 };

export const statusColors = (theme, kind = 'neutral') => {
  if (!isDark(theme)) {
    const key = statusTokens[kind] ? kind : 'neutral';
    const tokens = statusTokens[key];
    const amount = LIGHT_TEXT_DARKEN[key];
    return {
      bg: tokens.background,
      text: amount ? darken(tokens.text, amount) : tokens.text,
      border: tokens.dot,
    };
  }
  if (kind === 'neutral' || !PALETTE_KEY[kind]) {
    return {
      bg: theme.palette.surface.containerHighest,
      text: theme.palette.text.secondary,
      border: theme.palette.outline.main,
    };
  }
  const main = theme.palette[PALETTE_KEY[kind]].main;
  return {
    bg: alpha(main, 0.22),
    text: kind === 'info' ? main : lighten(main, 0.45),
    border: kind === 'info' ? main : lighten(main, 0.2),
  };
};

/** sx for a small status Chip. */
export const statusChipSx = (kind) => (theme) => {
  const { bg, text } = statusColors(theme, kind);
  return {
    bgcolor: bg,
    color: text,
    fontWeight: 600,
    fontSize: FONT.caption,
    '& .MuiChip-icon': { color: text },
  };
};

/**
 * Selectable option card (AND/OR pickers). Selected cards take the status
 * colour; unselected ones stay on the paper surface.
 */
export const optionCardSx = (theme, { selected, kind }) => {
  const { bg, border } = statusColors(theme, kind);
  return {
    borderRadius: '10px',
    border: '2px solid',
    borderColor: selected ? border : theme.palette.outline.variant,
    bgcolor: selected ? bg : 'background.paper',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      borderColor: selected ? border : theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  };
};

/**
 * Icon button tinted with a palette colour (primary or error). In dark mode
 * error.main is about 2.3:1 on its own tint, so error buttons take dangerText.
 */
export const tintedIconButtonSx = (color = 'primary') => (theme) => ({
  color: color === 'error' ? dangerText(theme) : theme.palette[color].main,
  bgcolor: alpha(theme.palette[color].main, isDark(theme) ? 0.16 : 0.06),
  '&:hover': {
    bgcolor: alpha(theme.palette[color].main, isDark(theme) ? 0.26 : 0.12),
  },
});

/** Text colour for destructive actions; error.main is too dark on dark paper. */
export const dangerText = (theme) =>
  isDark(theme) ? lighten(theme.palette.error.main, 0.45) : theme.palette.error.main;
